import axios from "axios";
import { split } from "lodash";

/**
 * validateImageForExtension checks if the provided image meets the criteria for generative extension.
 * It checks both the file size and the dimensions of the image against predefined limits.
 * @param {*} image
 * @returns
 */
export const validateImageForExtension = (image) => {
  // Check if the size of the image exceeds the maximum limit of 9.5MB
  const maxSizeInBytes = 9.5 * 1024 * 1024; // 9.5MB
  if (image.size > maxSizeInBytes) {
    console.error("Image size exceeds the maximum limit of 9.5MB.");
    return false;
  }

  // Check if the dimensions of the image exceed the maximum limit of 9437184 pixels
  const maxPixels = 9437184;
  if (image.width * image.height > maxPixels) {
    console.error("Image dimensions exceed the maximum limit of 9437184 pixels.");
    return false;
  }

  // If both checks pass, the image is valid for extension
  return true;
};

/**
 * generateDataUrlFromImage takes an image file and converts it into a data URL using the FileReader API.
 * It returns a promise that resolves with the data URL or rejects with an error if the conversion fails.
 * @param {*} image
 * @returns {Promise<string>} A promise that resolves with the data URL of the image.
 */
export const generateDataUrlFromImage = (image) => {
  return new Promise((resolve, reject) => {
    if (image instanceof HTMLImageElement) {
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(image, 0, 0);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
      resolve(dataUrl);
    }
    else if (image instanceof File) {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(image);
    }
    else {
      reject(new Error('Unsupported image type. Please provide an HTMLImageElement or a File.'));
    }
  });
};

/**
 * dataUrlToBlob converts a data URL back into a Blob object, which can be used for file uploads or other operations that require binary data.
 * It parses the data URL to extract the MIME type and the binary data, and then creates a Blob from that data.
 * @param {*} dataUrl
 * @returns {Blob} A Blob object representing the binary data of the image.
 */
export const dataUrlToBlob = (dataUrl) => {
  const splitDataUrl = dataUrl.split(',');
  const byteString = splitDataUrl[0].indexOf('base64') >= 0 ? atob(splitDataUrl[1]) : decodeURIComponent(splitDataUrl[1]);
  const mimeString = splitDataUrl[0].split(':')[1].split(';')[0];
  const buffer = new ArrayBuffer(byteString.length);
  const intBuffer = new Uint8Array(buffer);
  for (let i = 0; i < byteString.length; i++) {
    intBuffer[i] = byteString.charCodeAt(i);
  }
  return new Blob([buffer], { type: mimeString });
};

/**
 * Extend the image using the generative image service. It sends a POST request to the backend with the image data URL.
 * The backend then processes the image and returns an extended version of it.
 * The function handles both the request and response, and logs relevant information for debugging.
 * @param {*} imageDataUrl
 * @returns
 */
export const extendImageGenerative = async (imageDataUrl) => {
  try {
    // console.log('Sending request to generative image service with image data URL:', imageDataUrl);
    const response = await axios.post('/api/edition/generative-image/extend', { imageDataUrl, factor: 1.2 });
    // console.log('Received response from generative image service:', response.data);
    return response.data.extended_image_data_url;
  } catch (error) {
    console.error('Error extending image:', error);
    throw error;
  }
};

/**
 * generateImageFromPrompt sends a POST request to the backend with a text prompt and aspect ratio to generate an image.
 * The backend processes the prompt using a generative model and returns the generated image as a data URL.
 * The function handles both the request and response, and logs relevant information for debugging.
 * @param {*} prompt
 * @param {*} aspectRatio
 * @returns {Promise<string>} A promise that resolves with the data URL of the generated image.
 */
export const generateImageFromPrompt = async (prompt, aspectRatio, referenceImages = []) => {
  try {
    const response = await axios.post('/api/edition/generative-image/generate', { prompt, aspectRatio, referenceImages });
    return response.data;
  } catch (error) {
    console.error('Error generating image from prompt:', error);
    throw error;
  }
};

/** getImageGenerationPrice sends a GET request to the backend to retrieve the current price for generating an image using the generative image service.
 * The backend returns the price, which can be used to inform users about the cost of generating an image before they proceed with the generation process.
 * The function handles both the request and response, and logs relevant information for debugging.
 * @returns {Promise<number>} A promise that resolves with the price for generating an image.
 */
export const getImageGenerationPrice = async () => {
  try {
    const response = await axios.get('/api/edition/generative-image/cost');
    return response.data.price;
  } catch (error) {
    console.error('Error fetching image generation price:', error);
    throw error;
  }
};

/**
 * Get the user's image generation price info by sending a GET request to the backend. This includes information about the user's current credits and the cost of generating an image.
 * The backend returns the user's price info, which can be used to inform users about their ability to generate images based on their current credits and the cost of generation.
 * The function handles both the request and response, and logs relevant information for debugging.
 * @returns {Promise<Object>} A promise that resolves with the user's image generation price info.
 */
export const getUserPriceInfoAction = async () => {
  try {
    const response = await axios.get('/api/edition/generative-image/user-price-info');
    return response.data;
  } catch (error) {
    console.error('Error fetching user image generation price info:', error);
    throw error;
  }
};